import type { RecordSummary } from "@shared/recordSummary";
import type { CoiDetailPayload } from "@/hooks/useCoiDetail";
import { touchLabel } from "@/components/coi/specialty";
import type { Fact } from "../FactsStrip";
import { dayLabel, firstName } from "../recordFormat";

const KIND = { call: "Call", meeting: "Meeting", conference: "Conference" } as const;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const money = (n: number) => (n >= 1_000_000 ? `≈ $${(n / 1_000_000).toFixed(1)}M` : `≈ $${Math.round(n / 1000)}K`);

export function lastSpokeFact(s: RecordSummary | undefined, today: string): Fact {
  const c = s?.lastConversation;
  if (!c) return { label: "Last spoke", value: "Not yet", sub: "No call or meeting logged" };
  const kind = c.meal ? "Lunch" : KIND[c.type];
  return { label: "Last spoke", value: dayLabel(c.date, today), sub: c.loggedBy ? `${kind}, ${c.loggedBy}` : kind };
}

export function lastEmailFact(s: RecordSummary | undefined, today: string): Fact {
  const e = s?.lastEmail;
  if (!e) return { label: "Last email", value: "None synced" };
  return { label: "Last email", value: dayLabel(e.date, today), sub: `${firstName(e.from)} → ${firstName(e.to)}: ${e.subject}` };
}

export function openItemsFact(s: RecordSummary | undefined): Fact {
  const items = s?.openItems ?? [];
  if (!items.length) return { label: "Open items", value: "None" };
  const late = items.filter((i) => i.overdue).length;
  return late
    ? { label: "Open items", value: String(items.length), sub: `${late} overdue`, tone: "bad" }
    : { label: "Open items", value: String(items.length), sub: `Next: ${items[0].title}` };
}

export function ftcContactFact(s: RecordSummary | undefined): Fact {
  const rows = s?.ftcContact ?? [];
  if (!rows.length) return { label: "FTC contact", value: "Nobody yet" };
  const top = rows[0];
  const counts = [top.emails ? plural(top.emails, "email") : null, top.conversations ? plural(top.conversations, "conversation") : null]
    .filter(Boolean).join(", ");
  const others = rows.slice(1, 3).map((r) => firstName(r.name));
  return { label: "FTC contact", value: firstName(top.name), sub: others.length ? `${counts} · also ${others.join(", ")}` : counts };
}

export function processesFact(s: RecordSummary | undefined): Fact | null {
  const rows = s?.processes;
  if (!rows) return null;
  const active = rows.filter((r) => r.active);
  if (!active.length) return { label: "In processes", value: rows.length ? "None active" : "None", sub: rows.length ? `${rows.length} past` : null };
  return {
    label: "In processes",
    value: `${active.length} active`,
    sub: active.slice(0, 2).map((r) => `${r.projectName ?? r.clientName} (${r.furthest ?? "added"})`).join(", "),
  };
}

/** Facts for a contact who is not a COI. */
export function contactFacts(s: RecordSummary | undefined, today: string, company: { name: string; category: string } | null): Fact[] {
  const facts = [lastSpokeFact(s, today), lastEmailFact(s, today), openItemsFact(s)];
  const p = processesFact(s);
  if (p) facts.push(p);
  else if (company) facts.push({ label: "Company", value: company.name, sub: company.category });
  facts.push(ftcContactFact(s));
  return facts;
}

export type CoiFactsInput = Pick<
  CoiDetailPayload,
  "lastTouch" | "daysSinceTouch" | "lastTouchBy" | "introsOpen" | "introsSigned" | "sentCount" | "sectors" | "mandatesFeeOpportunity" | "sent"
>;

/** The COI page's existing five facts (no grade), plus who at FTC talks to them. */
export function coiFacts(coi: CoiFactsInput, s: RecordSummary | undefined): Fact[] {
  const latestSent = coi.sent[0];
  const fee = coi.mandatesFeeOpportunity;
  return [
    {
      label: "Last touch",
      value: touchLabel(coi.lastTouch, coi.daysSinceTouch),
      sub: coi.lastTouchBy ? `logged by ${coi.lastTouchBy}` : coi.lastTouch?.kind === "email" ? "synced" : null,
    },
    { label: "Intros to us", value: String(coi.introsOpen + coi.introsSigned), sub: `${coi.introsOpen} open · ${coi.introsSigned} signed` },
    { label: "Mandates", value: String(coi.introsSigned), sub: fee ? `${money(fee)} fees` : null, gated: !!fee },
    { label: "Sent to them", value: String(coi.sentCount), sub: latestSent ? latestSent.title.replace(/^Referral sent: /, "") : null },
    { label: "Sectors", value: coi.sectors.length ? coi.sectors.slice(0, 2).join(", ") : "None yet", sub: coi.sectors.length ? "from their referrals" : null },
    ftcContactFact(s),
  ];
}
